import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store, select } from '@ngrx/store';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';

import { getLoggedInUser, updateUser, updateUserFail } from 'ish-core/store/customer/user';

import { accountNavigationFavoritesActions } from './account-navigation-favorites.actions';
import { getAccountNavigationFavoriteIds } from './account-navigation-favorites.selectors';

/**
 * The favorites are stored as a custom attribute of the user profile, so they arrive with the user and
 * are written back through the existing user update. No own resource and no own request is involved:
 * loading is a pure projection of the profile that is already in the store.
 */
@Injectable()
export class AccountNavigationFavoritesEffects {
  constructor(private actions$: Actions, private store: Store) {}

  /**
   * Takes over the favorites whenever the logged in user changes, on login, on a profile update and
   * after a page reload that restores the session.
   */
  syncAccountNavigationFavorites$ = createEffect(() =>
    this.store.pipe(
      select(getLoggedInUser),
      map(user => user?.favoriteIds ?? []),
      distinctUntilChanged((a, b) => a.length === b.length && a.every((id, index) => id === b[index])),
      map(favoriteIds => accountNavigationFavoritesActions.syncAccountNavigationFavorites({ favoriteIds }))
    )
  );

  /**
   * Saves the toggled favorites with the profile of the user. The reducer has already applied the
   * toggle, so the current state is what is sent. No success message is passed on, a favorite is not
   * worth a notification.
   */
  saveAccountNavigationFavorites$ = createEffect(() =>
    this.actions$.pipe(
      ofType(accountNavigationFavoritesActions.toggleAccountNavigationFavorite),
      concatLatestFrom(() => [
        this.store.pipe(select(getAccountNavigationFavoriteIds)),
        this.store.pipe(select(getLoggedInUser)),
      ]),
      filter(([, , user]) => !!user),
      map(([, favoriteIds, user]) => updateUser({ user: { ...user, favoriteIds } }))
    )
  );

  /**
   * A rejected profile update leaves the user in the store untouched, so the optimistic toggle is
   * rolled back by taking the favorites of that profile over again.
   */
  rollbackAccountNavigationFavorites$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateUserFail),
      concatLatestFrom(() => this.store.pipe(select(getLoggedInUser))),
      map(([, user]) =>
        accountNavigationFavoritesActions.syncAccountNavigationFavorites({ favoriteIds: user?.favoriteIds ?? [] })
      )
    )
  );
}
